import React, {
    useEffect,
    useState,
} from "react";

import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import ReactGA from "react-ga4";

import {
    Link,
    useNavigate,
} from "react-router-dom";

import { useDispatch } from "react-redux";

import Layout from "../components/layoutBlack";
import Breadcrumbs from "../components/breadcrumbs";
import EmailIcon from "../assets/images/icons/icon-email.png";

import { loginSuccess,} from "../store/slices/auth.ts";
import { config } from "../utils/constants";

const REACT_APP_API_URL =
    config.url.REACT_APP_API_URL;


const getErrorText = (error) => {
    if (!error) {
        return "";
    }

    if (Array.isArray(error)) {
        return error
            .map(getErrorText)
            .filter(Boolean)
            .join(" ");
    }

    if (typeof error === "string") {
        return error;
    }

    if (typeof error === "object") {
        return Object.values(error)
            .map(getErrorText)
            .filter(Boolean)
            .join(" ");
    }

    return "";
};


const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [
        serverMessage,
        setServerMessage,
    ] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const formik = useFormik({
        initialValues: {
            email: "",
            password: "",
        },

        validationSchema: Yup.object({
            email: Yup.string()
                .trim()
                .email(
                    "Enter a valid email address"
                )
                .required(
                    "Email is required"
                ),

            password: Yup.string()
                .required(
                    "Password is required"
                ),
        }),

        onSubmit: async (
            values,
            {
                setSubmitting,
            }
        ) => {
            setServerMessage("");

            try {
                const response = await axios.post(
                    `${REACT_APP_API_URL}/auth/login/`,
                    {
                        email: values.email
                            .trim()
                            .toLowerCase(),

                        password:
                            values.password,
                    },
                    {
                        withCredentials: true,
                    }
                );

                console.log(
                    "Login response:",
                    response.status,
                    response.data
                );

                const accessToken =
                    response.data?.access;

                const user =
                    response.data?.user;

                /*
                * Do not throw a normal JavaScript error here,
                * because it would be mistaken for a network failure.
                */
                if (!accessToken || !user) {
                    console.error(
                        "Unexpected login response:",
                        response.data
                    );

                    setServerMessage(
                        "The server accepted the login but did not return the expected account information."
                    );

                    return;
                }

                dispatch(
                    loginSuccess({
                        access: accessToken,
                        user,
                    })
                );

                ReactGA.event({
                    category: "account",
                    action: "login",
                    label:
                        "Wholesale Account Login",
                });

                navigate(
                    "/wholesale/catalog",
                    {
                        replace: true,
                    }
                );
            } catch (error) {
                console.error(
                    "Login request failed:",
                    error
                );

                /*
                * A true Axios error may contain a response from
                * Django, or no response when the server/CORS failed.
                */
                if (axios.isAxiosError(error)) {
                    const responseData =
                        error.response?.data;

                    if (!error.response) {
                        setServerMessage(
                            "The login server could not be reached. Please verify that the backend is running and the API URL is correct."
                        );

                        return;
                    }

                    const backendMessage =
                        getErrorText(
                            responseData?.detail
                        ) ||
                        getErrorText(
                            responseData?.error
                        ) ||
                        getErrorText(
                            responseData
                                ?.non_field_errors
                        ) ||
                        getErrorText(
                            responseData?.email
                        ) ||
                        getErrorText(
                            responseData?.password
                        );

                    if (backendMessage) {
                        setServerMessage(
                            backendMessage
                        );

                        return;
                    }

                    if (
                        error.response.status ===
                        401
                    ) {
                        setServerMessage(
                            "The email address or password is incorrect."
                        );

                        return;
                    }

                    if (
                        error.response.status ===
                        403
                    ) {
                        setServerMessage(
                            "Your wholesale account has not been approved for login."
                        );

                        return;
                    }

                    setServerMessage(
                        `Login failed with server status ${error.response.status}.`
                    );

                    return;
                }

                setServerMessage(
                    error instanceof Error
                        ? error.message
                        : "An unexpected login error occurred."
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <div>
            <Layout>
                {/* Breadcrumbs */}
                <div className="container p-t-80 p-b-10">
                    <Breadcrumbs />
                </div>

                {/* Page heading */}
                <section className="bg0 p-t-30 p-b-20">
                    <div className="container">
                        <div className="txt-center">
                            <h2 className="ltext-105 cl2">
                                Wholesale Login
                            </h2>

                            <p className="stext-115 cl6 p-t-15">
                                Sign in to access your
                                approved Molu Wellness
                                wholesale account.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Main content */}
                <section className="full-page-section bg0 p-t-30 p-b-125">
                    <div className="container">
                        <div className="flex-w flex-tr">

                            {/* Login form */}
                            <div className="size-210 bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg w-full-md">
                                <div className="w-full">
                                    <h4 className="mtext-105 cl2 txt-center p-b-15">
                                        Wholesale Account
                                        Login
                                    </h4>

                                    <p className="stext-102 cl6 txt-center p-b-30">
                                        Enter the email
                                        address and password
                                        associated with your
                                        approved wholesale
                                        account.
                                    </p>

                                    {serverMessage ? (
                                        <div
                                            className="alert alert-danger m-b-25"
                                            role="alert"
                                            aria-live="polite"
                                        >
                                            {serverMessage}
                                        </div>
                                    ) : null}

                                    <form
                                        method="post"
                                        onSubmit={
                                            formik.handleSubmit
                                        }
                                        noValidate
                                    >
                                        {/* Email */}
                                        <div className="m-b-20">
                                            <label
                                                htmlFor="email"
                                                className="stext-102 cl3 p-b-8"
                                                style={{
                                                    display:
                                                        "block",
                                                }}
                                            >
                                                Email Address
                                            </label>

                                            <div className="bor8 how-pos4-parent">
                                                <input
                                                    id="email"
                                                    className="stext-111 cl2 plh3 size-116 p-l-62 p-r-30"
                                                    type="email"
                                                    name="email"
                                                    placeholder="Email address"
                                                    autoComplete="email"
                                                    value={
                                                        formik
                                                            .values
                                                            .email
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={
                                                        formik.handleBlur
                                                    }
                                                />

                                                <img
                                                    className="how-pos4 pointer-none"
                                                    src={
                                                        EmailIcon
                                                    }
                                                    alt=""
                                                />
                                            </div>

                                            {formik.touched
                                                .email &&
                                            formik.errors
                                                .email ? (
                                                <div className="text-danger mt-1 text-sm p-t-5">
                                                    {
                                                        formik
                                                            .errors
                                                            .email
                                                    }
                                                </div>
                                            ) : null}
                                        </div>

                                        {/* Password */}
                                        <div className="m-b-20">
                                            <label
                                                htmlFor="password"
                                                className="stext-102 cl3 p-b-8"
                                                style={{
                                                    display:
                                                        "block",
                                                }}
                                            >
                                                Password
                                            </label>

                                            <div className="bor8">
                                                <input
                                                    id="password"
                                                    className="stext-111 cl2 plh3 size-116 p-l-30 p-r-30"
                                                    type="password"
                                                    name="password"
                                                    placeholder="Password"
                                                    autoComplete="current-password"
                                                    value={
                                                        formik
                                                            .values
                                                            .password
                                                    }
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                    onBlur={
                                                        formik.handleBlur
                                                    }
                                                />
                                            </div>

                                            {formik.touched
                                                .password &&
                                            formik.errors
                                                .password ? (
                                                <div className="text-danger mt-1 text-sm p-t-5">
                                                    {
                                                        formik
                                                            .errors
                                                            .password
                                                    }
                                                </div>
                                            ) : null}
                                        </div>

                                        {/* Forgot password */}
                                        <div className="txt-right p-b-25">
                                            <Link
                                                className="stext-102 cl1 hov-cl1 trans-04"
                                                to="/request-password-reset"
                                            >
                                                Forgot your
                                                password?
                                            </Link>
                                        </div>

                                        {/* Submit */}
                                        <button
                                            className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer"
                                            type="submit"
                                            disabled={
                                                formik.isSubmitting
                                            }
                                        >
                                            {formik.isSubmitting
                                                ? "Signing In..."
                                                : "Sign In"}
                                        </button>

                                        {/* Registration */}
                                        <div className="txt-center p-t-30">
                                            <p className="stext-102 cl6 p-b-10">
                                                Interested in
                                                becoming a Molu
                                                wholesale
                                                partner?
                                            </p>

                                            <Link
                                                to="/wholesale-registration"
                                                className="stext-101 cl1 hov-cl1 trans-04"
                                            >
                                                Submit a
                                                Wholesale
                                                Application
                                            </Link>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {/* Login information */}
                            <div className="size-210 bor10 flex-w flex-col-m p-lr-70 p-tb-50 p-lr-15-lg w-full-md">
                                <div className="w-full">
                                    <h3 className="mtext-110 cl2 p-b-25">
                                        Molu Wholesale
                                        Partners
                                    </h3>

                                    <p className="stext-115 cl6 p-b-35">
                                        Approved wholesale
                                        partners can sign in
                                        to view wholesale
                                        pricing, manage
                                        account information,
                                        and place wholesale
                                        orders.
                                    </p>

                                    {/* Approved account */}
                                    <div className="flex-w w-full p-b-35">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-checkmark-circle" />
                                        </span>

                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">
                                                Approved
                                                Accounts
                                            </span>

                                            <p className="stext-115 cl6 size-213 p-t-12">
                                                Login access
                                                becomes
                                                available
                                                after your
                                                wholesale
                                                application
                                                has been
                                                reviewed and
                                                approved.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Pending applications */}
                                    <div className="flex-w w-full p-b-35">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-hourglass" />
                                        </span>

                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">
                                                Pending
                                                Applications
                                            </span>

                                            <p className="stext-115 cl6 size-213 p-t-12">
                                                Applicants
                                                normally
                                                receive an
                                                approval
                                                decision by
                                                email within
                                                48 hours.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Email verification */}
                                    <div className="flex-w w-full p-b-35">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-envelope" />
                                        </span>

                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">
                                                Email
                                                Verification
                                            </span>

                                            <p className="stext-115 cl6 size-213 p-t-12">
                                                Existing Molu
                                                customers must
                                                complete email
                                                verification
                                                before their
                                                wholesale
                                                application is
                                                submitted for
                                                review.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Support */}
                                    <div className="flex-w w-full">
                                        <span className="fs-18 cl5 txt-center size-211">
                                            <span className="lnr lnr-question-circle" />
                                        </span>

                                        <div className="size-212 p-t-2">
                                            <span className="mtext-110 cl2">
                                                Wholesale
                                                Support
                                            </span>

                                            <p className="stext-115 cl1 size-213 p-t-12">
                                                <a
                                                    href="mailto:support@molu.co"
                                                    className="cl1"
                                                >
                                                    support@molu.co
                                                </a>
                                            </p>

                                            <p className="stext-115 cl1 size-213 p-t-8">
                                                <a
                                                    href="tel:+19722838880"
                                                    className="cl1"
                                                >
                                                    +1 972 283
                                                    8880
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>
            </Layout>
        </div>
    );
};

export default Login;